<template>
	<div class="content-page content-page--customer-error-logs">
		<ActionSetNav
			v-if="customerName"
			class="navigation-bar__actions"
			:name="customerName"
			:display="true"
			:actions="mainNavActions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Error Log
					<span
						class="content-page__title--detail"
						v-if="customerName"
						>- {{ customerName }}</span
					>

					<span
						class="content-page__title--icon"
						ref="refreshIconRef"
						@mouseover="tooltipRefreshVisible = true"
						@mouseleave="tooltipRefreshVisible = false"
						><Button
							:className="'btn--icon-only-circle'"
							iconClassName="icon-refresh"
							:iconBeforeLabel="true"
							:btnClick="
								() => {
									refreshErrorLog();
								}
							"
							data-test="refreshButton"
						/>
						<Tooltip
							:text="'Refresh the log (selections will be lost)'"
							:display="tooltipRefreshVisible"
							:trackedRefName="'refreshIconRef'"
							position="top"
							:offset="[0, 12]"
						/>
					</span>
				</h2>
			</div>
			<div class="content-page__actions">
				<Button
					v-show="resultCounter"
					className="btn--has-icon btn--small"
					iconClassName="icon-export"
					labelText="Export"
					:btnClick="downloadOnCSV"
					:iconBeforeLabel="true"
					data-test="exportButton"
				/>
			</div>
		</div>

		<ErrorLogsFilters
			v-model="filters"
			:defaultFilters="defaultFilters"
			:userOptions="userOptions"
			:statusOptions="errorStatuses"
			:datePickerMaxDate="maxDatetimeAllowed"
			:disableDaysAfterMaxDate="true"
			:counter="resultCounter"
			@search-users="searchUsers"
			@reset-filters="updateUserOptionsFromErrorLogs()"
			@clean-search="updateUserOptionsFromErrorLogs()"
			@resetSelectedIds="
				() => {
					selectedIds = [];
				}
			"
			ref="errorLogsFilterRef"
			:markButtonOpts="markButtonOpts"
			:selectedIds="selectedIds"
			@optionSelected="updateDataResolvedBulk"
		/>

		<ScrollContainer v-if="hasResults">
			<Table
				class="table-container--error-logs"
				:sourceObject="tableSource"
				:isArrowUp="true"
				@change="updatedPagination"
				ref="tableErrorLogs"
				:tableMoev1="true"
			>
				<template #header-resolved>
					<label class="readonly-field__label"
						><span>Resolved</span>
						<div class="readonly-field__label--tooltip">
							<TooltipContainer
								:tooltip="
									'Use bulk action button to resolve multiple errors'
								"
								:position="'top-end'"
							>
								<template #container>
									<IconInfo position="top" />
								</template>
							</TooltipContainer>
						</div>
					</label>
				</template>
				<template #col-group>
					<colgroup>
						<!-- Select -->
						<col class="table__col table__col--select" />

						<!-- Date/Time -->
						<col class="table__col table__col--date-time" />

						<!-- User -->
						<col class="table__col table__col--user-id" />

						<!-- Details -->
						<col class="table__col table__col--error-details" />

						<!-- Comment -->
						<col class="table__col table__col--resolved-comment" />
						<!-- Azure Tickets -->
						<col
							class="table__col table__col--resolved-azure-ticket"
						/>
						<!-- Resolved -->
						<col class="table__col table__col--resolved" />
					</colgroup>
				</template>
				<template #body-select="{ body, index }">
					<!-- DEV NOTE: cycle through ref to get all the logs ids -->
					<div class="error-logs-data__select">
						<Checkbox
							:ref="`selectedRow${index}`"
							:name="`${uid}_checbox`"
							:value="body.logId"
							@input="addValueIntoSelectedIds"
							:modelValue="isSelectedCheckbox(body.logId)"
						/>
					</div>
				</template>
				<template #body-timestamp="{ body }">
					<div class="error-logs-data">
						{{ fullDateString(body.timestamp, true) }}
						<div class="error-logs-data__text-emphasis">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-userId="{ body }">
					<div class="error-logs-data__user">
						{{ body.userId }}
					</div>
				</template>
				<template #body-errorDetail="{ body }">
					<div class="error-logs-data__details">
						<div class="error-logs-data__text">
							<div
								class="error-logs-data__text_errorCode"
								:ref="getColumnRef(body.logId, 'errorCode')"
							>
								{{
									generateErrorLogDetails({
										errorCode: body.errorCode
									})
								}}
							</div>
							<div
								class="error-logs-data__text_correlationId"
								:ref="getColumnRef(body.logId, 'correlationId')"
							>
								{{
									generateErrorLogDetails({
										CorrelationId: body.correlationId
									})
								}}
							</div>
							<div
								class="error-logs-data__text_errorDetail"
								:ref="getColumnRef(body.logId, 'errorDetail')"
							>
								{{
									generateErrorLogDetails({
										errorDetail: body.errorDetail
									})
								}}
							</div>

							<Button
								v-if="
									isColumnTruncated(body.logId, 'errorDetail')
								"
								labelText="read more"
								className="btn--inline-link"
								:btnClick="() => showReadMoreDetailsModal(body)"
							/>
						</div>
						<div class="error-logs-data__action">
							<Button
								className="btn--icon-only"
								iconClassName="icon-copy"
								:iconBeforeLabel="true"
								:btnClick="() => copyToClipboardFn(body)"
							/>
						</div>
					</div>
				</template>
				<!-- table comment column -->
				<template #body-resolvedComment="{ body }">
					<div class="error-logs-data__comment">
						<div
							v-if="body.resolvedComment.length"
							class="error-logs-data__text"
						>
							{{ detailsTextTruncated(body.resolvedComment) }}
							<Button
								v-if="
									body.resolvedComment.length >
										maxTextCharQuantity
								"
								labelText="read more"
								className="btn--inline-link"
								:btnClick="
									() =>
										displayErrorLogSaveEditModal(
											body.logId,
											'read',
											'readMore'
										)
								"
							/>
						</div>
						<div v-else class="error-logs-data__text">
							{{ body.resolvedComment }}
						</div>
						<div class="error-logs-data__action">
							<Button
								className="btn--icon-only"
								:iconClassName="
									body.resolvedComment
										? 'icon-edit'
										: 'icon-more'
								"
								:iconBeforeLabel="true"
								:btnClick="
									() =>
										displayErrorLogSaveEditModal(
											body.logId,
											body.resolvedComment
												? 'edit'
												: 'add',
											'editButton'
										)
								"
							/>
						</div>
					</div>
				</template>
				<template #body-resolvedAzureDevOpsUrl="{body}">
					<div class="error-logs-data__azureDevOpsUrl">
						<div class="error-logs-data__text">
							<a
								class="link"
								v-on:click.prevent="
									clickAzureTicketHandler(
										body.resolvedAzureDevOpsUrl
									)
								"
							>
								{{ body.resolvedAzureDevOpsUrl }}
							</a>
						</div>
						<div class="error-logs-data__action">
							<Button
								className="btn--icon-only"
								data-test="azureTicketBtn"
								:iconClassName="
									body.resolvedAzureDevOpsUrl
										? 'icon-edit'
										: 'icon-more'
								"
								:iconBeforeLabel="true"
								:btnClick="
									() =>
										displayErrorLogAzureTicketModal(
											body.logId,
											body.resolvedAzureDevOpsUrl
												? 'edit'
												: 'add',
											'editButton'
										)
								"
							/>
						</div>
					</div>
				</template>
				<template #body-resolved="{ body }">
					<div class="error-logs-data">
						<TooltipContainer
							class="error-logs-data__resolvedUser"
							:tooltip="
								body.resolvedUserId
									? `By ${body.resolvedUserId}`
									: ''
							"
							:position="'top-end'"
						>
							<template #container>
								<Toggle
									:value="body.resolved"
									@change="updateDataResolved(body)"
									:disabled="selectedIds.length > 0"
								/>
							</template>
						</TooltipContainer>
					</div>
				</template>
			</Table>
		</ScrollContainer>
		<ErrorLogCommentSaveModal
			class="impact-form__footer-btns"
			v-model="showErrorLogCommentModal"
			:resolvedComment="resolvedComment"
			:action="actionModel"
			:errorsToResolve="errorsToResolve"
			:selectedIds="selectedIds"
			:errorLogInfo="errorLogInfo"
			@backToogleData="handleBackToggleData"
			@save="saveComment"
		/>
		<ErrorLogAzureTicketModal
			v-model="showErrorLogAzureTicketModal"
			:resolvedAzureDevOpsUrl="resolvedAzureDevOpsUrl"
			:action="actionModel"
			@save="saveAzureTicket"
		/>
		<ErrorLogDetailsModal
			v-model="showErrorLogDetailsModal"
			:errorLogDetails="errorLogDetails"
			@copy="copyToClipboardFn"
		/>
		<Spinner v-if="loading" :processor="{ loading: true }" />
		<OverlayBackdrop v-if="loading"></OverlayBackdrop>
		<NoResults v-if="!hasResults && !loading" />
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import ErrorLogsFilters from './error-logs-filters';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Button from '@/components/forms/button/button';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import NoResults from '@/site-components/no-results/no-results';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import PageKeyNames from '@/utils/page-key-names';
import ErrorLogCommentSaveModal from '@/sections/customers/error-logs/error-log-comment-save-modal';
import OverlayBackdrop from '@/components/visual/overlay-backdrop';
import Tooltip from '@/components/shells/tooltip/tooltip';
import ErrorLogAzureTicketModal from '@/sections/customers/error-logs/error-log-devops-ticket-modal.vue';
import ErrorLogDetailsModal from '@/sections/customers/error-logs/error-log-details-modal';
import IconInfo from '@/components/notifications/icon-info/icon-info';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues,
	deepEqual
} from '@/utils/object';

import { parseDateToUTC } from '@/utils/date/parseDate';
import { devOpsDefaultUrl } from '@/constants/devOpsDefaultUrl.js';
import headerOptions from '@/sections/customers/error-logs/error-logs-header-options.js';
// mixins
import DateTimeMixin from '@/mixins/datetime-mixin';
import CustomerCardActions from '@/mixins/customers/customer-card-action';
import TooltipContainer from '@/components/shells/tooltip/tooltip-container.vue';

// vuex
import { mapState, mapGetters, mapActions } from 'vuex';

// libraries
import _ from 'lodash';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import { copyToClipboard } from '@/utils/function';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox.vue';
import resize from 'vue-resize-directive';

const COMMENT_MODAL = 'commentModal';
const AZURE_TICKET_MODAL = 'azureTicketModal';

export default {
	name: 'ErrorLogs',
	components: {
		ActionSetNav,
		ErrorLogsFilters,
		ScrollContainer,
		Breadcrumbs,
		Button,
		Checkbox,
		Toggle,
		NoResults,
		Spinner,
		Table,
		ErrorLogCommentSaveModal,
		OverlayBackdrop,
		Tooltip,
		ErrorLogAzureTicketModal,
		TooltipContainer,
		ErrorLogDetailsModal,
		IconInfo
	},
	mixins: [DateTimeMixin, CustomerCardActions],
	props: {
		id: {
			type: String,
			required: true
		},
		companyId: {
			type: Number,
			default: null
		},
		locationId: {
			type: Number,
			default: null
		},
		search: String,
		dateRange: Date,
		userId: {
			type: String,
			default: null
		},
		isResolved: {
			type: [Boolean, String],
			default: false
		},
		recordStart: {
			type: Number,
			default: 1
		},
		maxCount: {
			type: Number,
			default: 10
		}
	},
	directives: {
		resize
	},
	data() {
		return {
			loading: false,
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: '',
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				userId: null,
				isResolved: false,
				recordStart: 1,
				maxCount: 10
			},
			defaultUrlNames: [
				{
					url: `/clients`,
					name: 'Clients'
				},
				{
					name: 'Error Logs'
				}
			],
			customer: undefined,
			mainNavActions: [],
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			maxTextCharQuantity: 90,
			errorStatuses: [
				{ text: 'All Statuses', value: 'all' },
				{ text: 'Unresolved', value: false },
				{ text: 'Resolved', value: true }
			],
			downloadCSV: false,
			resolvedComment: '',
			showErrorLogCommentModal: false,
			showErrorLogAzureTicketModal: false,
			actionModel: '',
			userOptions: [],
			errorLogsClone: {},
			selectedIds: [],
			errorLogInfo: {},
			previousActionClicked: '',
			newFiltersWithPagination: {},
			selectedIdsMapped: [],
			tooltipRefreshVisible: false,
			resolvedAzureDevOpsUrl: '',
			showErrorLogDetailsModal: false,
			errorLogDetails: {},
			columnRefs: {},
			truncatedColumns: {}
		};
	},
	mounted() {
		this.checkAllColumnsTruncated();
		this.$store.commit('setCurrentPage', PageKeyNames.CUSTOMER_ERROR_LOGS);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	async created() {
		this.tableSource.headers = headerOptions;
		this.mainNavActions = this.actions;

		const companyId = this.getCompanyIds();

		const parameters = {
			DealershipGroupGuid: this.id,
			companyId: companyId,
			MinDatetime: parseDateToUTC(
				new Date(sub(new Date(), { months: 2 }))
			),
			MaxDatetime: parseDateToUTC(new Date(), true)
		};
		await this.getErrorLogsUsers(parameters).then(() => {
			this.userOptions = [...this.errorLogsUsersOptions];
		});
	},
	beforeRouteEnter(routeTo, routeFrom, next) {
		if (routeTo.fullPath !== routeFrom.fullPath) {
			next();
		} else {
			next(false);
		}
	},
	computed: {
		...mapState('customer', ['errorLogs', 'errorLogsUsersOptions']),
		...mapGetters('customers', ['customerById']),
		...mapGetters('ellipsisResize', ['resize']),
		customerName() {
			if (this.companyId || this.locationId) {
				if (!_.isEmpty(this.locationSelected)) {
					return (
						this.locationSelected?.locationName ||
						'No Location Name Provided'
					);
				}

				if (!_.isEmpty(this.companySelected)) {
					return (
						this.companySelected.companyName ||
						'No Company Name Provided'
					);
				}
			}
			return this.customer?.displayName || 'No Customer Name Provided';
		},
		resultCounter() {
			return this.tableSource.source?.totalCount;
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);
			return !_.isEmpty(routeBreadcrumb)
				? routeBreadcrumb
				: !_.isEmpty(storeBreadcrumb)
				? storeBreadcrumb
				: this.defaultUrlNames;
		},
		companySelected() {
			return _.isNil(this.companyId)
				? undefined
				: _.find(this.customer?.companies, {
						companyId: this.companyId
				  });
		},
		locationSelected() {
			const selectedCompany = this.companySelected;
			return _.isNil(selectedCompany)
				? undefined
				: _.find(selectedCompany.locations, {
						companyLocationId: this.locationId
				  });
		},
		errorsToResolve() {
			return this.selectedIds?.length || 0;
		},
		isSelectedCheckbox() {
			return logId => (this.selectedIds.includes(logId) ? logId : null);
		},
		resolvedOptions() {
			const {
				hasResolved,
				hasUnresolved
			} = this.selectedIdsMapped.reduce(
				(acc, { resolved }) => {
					resolved
						? (acc.hasResolved = true)
						: (acc.hasUnresolved = true);
					return acc;
				},
				{ hasResolved: false, hasUnresolved: false }
			);

			return [
				{
					text: 'Resolved',
					value: 'resolved',
					disabled: !hasUnresolved
				},
				{
					text: 'Unresolved',
					value: 'unresolved',
					disabled: !hasResolved
				}
			];
		},
		markButtonOpts() {
			return {
				disabled: this.selectedIds.length == 0,
				bulkActions: this.resolvedOptions
			};
		},
		maxDatetimeAllowed() {
			const localDate = new Date();
			const utcOffsetMs = localDate.getTimezoneOffset() * 60000;
			return new Date(localDate.getTime() + utcOffsetMs);
		}
	},
	methods: {
		...mapActions('customer', [
			'getErrorLogs',
			'getErrorLogsUsers',
			'saveErrorLogsResolutions'
		]),
		searchUsers(searchQuery) {
			this.updateUserOptionsFromErrorLogs();
			this.fetchUsers(searchQuery);
		},
		fetchUsers(searchQuery) {
			let usersFilter = [...this.userOptions];
			usersFilter = usersFilter.filter(user =>
				user.value.includes(searchQuery)
			);
			this.userOptions = usersFilter;

			const { errorLogsFilterRef } = this.$refs;

			const userDropdownRef = errorLogsFilterRef.$refs?.usersDropdownRef;
			if (userDropdownRef) {
				userDropdownRef.stopLoading();
			}
		},
		handleResize(e) {
			const size = parseInt(e.offsetWidth);
			if (this.resize !== size) {
				this.$store.commit('ellipsisResize/setResize', size);
			}
		},
		getCompanyIds() {
			if (this.companyId || this.locationId) {
				if (this.locationId) {
					return [this.locationId];
				} else {
					const companies = this.getAllLocationsByCompany(
						this.companyId
					);
					return [...companies];
				}
			}
			return [];
		},
		getAllLocationsByCompany(companyId) {
			if (
				companyId &&
				!this.locationId &&
				!_.isEmpty(this.companySelected)
			) {
				const locationIds = this.companySelected?.locations.map(
					location => location.companyLocationId
				);
				return [companyId, ...locationIds];
			}

			return [];
		},
		async loadErrorLogs(page) {
			if (_.isEmpty(this.customer)) {
				this.setCustomer();
			}
			if (!_.isEmpty(this.customer)) {
				const errorLogsParameters = {
					DealershipGroupGuid: this.customer.id,
					CompanyIds: this.getCompanyIds(),
					recordStart: page || 1
				};

				await this.fetchErrorLogs(errorLogsParameters);
			}
		},
		async fetchErrorLogs(parameters) {
			if (!this.downloadCSV && !this.loading) {
				this.loading = true;
			}

			let filteredQuery = removeObjectEmptyValues(this.filters);

			filteredQuery.maxCount = this.downloadCSV
				? 1000
				: this.filters.maxCount || 10;
			filteredQuery.recordStart = this.downloadCSV
				? this.defaultFilters.recordStart
				: filteredQuery.recordStart;

			filteredQuery.minDatetime = parseDateToUTC(
				filteredQuery.minDatetime
			);
			filteredQuery.maxDatetime = parseDateToUTC(
				filteredQuery.maxDatetime,
				true
			);

			filteredQuery = { ...parameters, ...filteredQuery };
			if (filteredQuery.isResolved === 'all') {
				delete filteredQuery.isResolved;
			}
			await this.getErrorLogs(filteredQuery)
				.then(() => {
					if (!_.isEmpty(this.errorLogs)) {
						this.errorLogsClone = _.cloneDeep(this.errorLogs);

						this.downloadCSV
							? this.exportCSV(this.errorLogsClone)
							: (this.tableSource.source = _.cloneDeep(
									this.errorLogsClone
							  ));
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		updatedPagination({ page, countPerPage }) {
			const newFilters = {
				recordStart: page,
				maxCount: countPerPage
			};

			const currentFilters = {
				recordStart: this.filters.recordStart,
				maxCount: this.filters.maxCount
			};

			if (!_.isEqual(newFilters, currentFilters)) {
				this.filters = { ...this.filters, ...newFilters };
			}
		},
		detailsTextTruncated(text) {
			if (text.length > this.maxTextCharQuantity) {
				return (
					text
						.split('')
						.slice(0, this.maxTextCharQuantity)
						.join('') + '...'
				);
			}
			return text;
		},
		displayErrorLogModal(id, action, actionClicked, modalName) {
			const errorLog = this.errorLogsClone.items.find(
				item => item.logId === id
			);
			if (!errorLog) return;

			this.mapErrorLogInfo(errorLog, modalName);
			this.actionModel = action;
			this.previousActionClicked = actionClicked;

			if (modalName === COMMENT_MODAL) {
				this.resolvedComment = errorLog.resolvedComment;
				this.showErrorLogCommentModal = true;
			}

			if (modalName === AZURE_TICKET_MODAL) {
				this.resolvedAzureDevOpsUrl = errorLog.resolvedAzureDevOpsUrl;
				this.showErrorLogAzureTicketModal = true;
			}
		},
		displayErrorLogSaveEditModal(id, action, actionClicked) {
			this.displayErrorLogModal(id, action, actionClicked, COMMENT_MODAL);
		},
		displayErrorLogAzureTicketModal(id, action, actionClicked) {
			this.displayErrorLogModal(
				id,
				action,
				actionClicked,
				AZURE_TICKET_MODAL
			);
		},
		copyToClipboardFn({ errorCode, correlationId, errorDetail }) {
			const formattedDetailLog = this.generateErrorLogDetails({
				errorCode,
				correlationId,
				errorDetail
			});
			return copyToClipboard(formattedDetailLog);
		},
		generateErrorLogDetails(errorLogDetails) {
			if (_.isEmpty(errorLogDetails)) {
				return '';
			}
			return Object.entries(errorLogDetails)
				.map(([key, value]) => {
					const formattedValue = JSON.stringify(value).replace(
						/"/g,
						''
					);
					return key !== 'errorDetail'
						? `${key}: ${formattedValue}`
						: formattedValue;
				})
				.join('\n');
		},
		async saveComment(comment) {
			const oidcUser = this.$store.getters['oidcStore/oidcUser'];
			const { Email: addedByUserId } = oidcUser;

			const resolvedActionMap = {
				add: this.errorLogInfo.resolved,
				edit: this.errorLogInfo.resolved,
				unresolved: false
			};

			const resolved = resolvedActionMap[this.actionModel] ?? true;

			let logTickets = this.errorLogInfo.logTickets;

			if (!logTickets && this.selectedIdsMapped.length) {
				logTickets = this.selectedIdsMapped.map(
					({ id, ticketNumber }) => ({ logId: id, ticketNumber })
				);
			}

			let payload = {
				addedByUserId,
				addedOn: new Date(),
				comment,
				resolved,
				action: this.actionModel,
				displayToast: true,
				logTickets
			};
			this.loading = true;
			await this.saveErrorLogsResolutions(payload).then(isSuccessful => {
				if (!isSuccessful) {
					this.loading = false;
				}
				if (isSuccessful) {
					this.clearErrorLogVariables();
					this.loadErrorLogs();
				}
			});
		},
		async saveAzureTicket(ticketNumber) {
			const oidcUser = this.$store.getters['oidcStore/oidcUser'];
			const { Email: addedByUserId } = oidcUser;

			let logTickets = this.errorLogInfo.logTickets.map(log => ({
				...log,
				ticketNumber
			}));

			let payload = {
				addedByUserId,
				addedOn: new Date(),
				comment: this.errorLogInfo.comment,
				resolved: this.errorLogInfo.resolved,
				action: this.actionModel,
				logTickets,
				displayToast: false
			};
			this.loading = true;
			await this.saveErrorLogsResolutions(payload).then(isSuccessful => {
				if (!isSuccessful) {
					this.loading = false;
				}
				if (isSuccessful) {
					this.clearErrorLogVariables();
					this.loadErrorLogs();
				}
			});
		},
		clearErrorLogVariables() {
			this.errorLogInfo = {};
			this.selectedIds = [];
			this.selectedIdsMapped = [];
		},
		updateRoute() {
			const { id, companyId, locationId, filters, defaultFilters } = this;

			let queries = removePropertiesWithSameValues(
				filters,
				defaultFilters
			);
			const pathSegments = [
				id ? `clients/${id}` : null,
				companyId ? `company/${companyId}` : null,
				locationId ? `location/${locationId}` : null
			].filter(Boolean);

			const path = `/${pathSegments.join('/')}/error-logs`;

			this.$router.push({ path, query: queries }).catch(() => {});
		},
		setCustomer() {
			this.customer = this.customerById(this.id);
			if (!_.isEmpty(this.customer)) {
				localStorage.setItem(
					'selectedCustomer',
					JSON.stringify(this.customer)
				);
			} else {
				const selectedCustomer = localStorage.getItem(
					'selectedCustomer'
				);
				if (!_.isEmpty(selectedCustomer)) {
					this.customer = JSON.parse(selectedCustomer);
				}

				// Add logic to call API with this id to get the customer information

				if (_.isEmpty(this.customer)) {
					this.$router.push({ name: 'clients', path: '/clients' });
					return;
				}
			}
		},
		updateUserOptionsFromErrorLogs() {
			if (
				JSON.stringify(this.userOptions) !=
				JSON.stringify(this.errorLogsUsersOptions)
			) {
				this.userOptions = [...this.errorLogsUsersOptions];
			}
		},
		mapErrorLogInfo(log, action) {
			const isModalAction =
				action === AZURE_TICKET_MODAL || action === COMMENT_MODAL;

			const logTickets = isModalAction
				? [
						{
							logId: log.logId,
							ticketNumber: log.resolvedAzureDevOpsUrl
						}
				  ]
				: this.selectedIdsMapped.map(({ id, ticketNumber }) => ({
						logId: id,
						ticketNumber
				  }));

			this.errorLogInfo = {
				resolvedUserId: log.resolvedUserId,
				resolvedTimestamp: log.resolvedTimestamp,
				resolved: log.resolved,
				comment: log.resolvedComment,
				logTickets
			};
		},
		updateDataResolved(body) {
			if (this.selectedIds.length) {
				this.selectedIds = [];
			}

			const actionName = resolved => {
				return resolved ? 'unresolved' : 'resolved';
			};

			const { logId, resolved, action = actionName(resolved) } = body;
			this.mapErrorLogInfo(body, actionName(resolved));

			this.addValueIntoSelectedIds(logId, true);
			this.displayErrorLogSaveEditModal(logId, action, 'toggleResolved');
			this.findAndToggleResolved(logId);
		},
		updateDataResolvedBulk(option) {
			this.previousActionClicked = 'markAsButton';
			this.actionModel = option;
			this.resolvedComment = '';
			this.showErrorLogCommentModal = true;
		},
		findAndToggleResolved(logId) {
			if (
				!this.errorLogsClone?.items ||
				this.previousActionClicked !== 'toggleResolved'
			)
				return;

			const logIndex = this.errorLogsClone.items.findIndex(
				item => item.logId === logId
			);
			if (logIndex === -1) return;

			const log = this.errorLogsClone.items[logIndex];
			log.resolved = !log.resolved;

			this.$set(this.errorLogsClone.items, logIndex, { ...log });
			this.tableSource.source = _.cloneDeep(this.errorLogsClone);
		},
		addValueIntoSelectedIds(value, isSelected) {
			if (isSelected) {
				if (!this.selectedIds.includes(value)) {
					this.selectedIds.push(value);
				}
			} else {
				this.selectedIds = this.selectedIds.filter(id => id !== value);
			}
			this.updateSelectedIdsMapped();
		},
		handleBackToggleData(logId) {
			if (this.selectedIds.includes(logId)) {
				this.selectedIds = this.selectedIds.filter(id => id !== logId);
			}
			this.findAndToggleResolved(logId);
		},
		compareFilters(objectToCompare) {
			const compare = ignoreProps => {
				return deepEqual(
					this.filters,
					objectToCompare,
					true,
					ignoreProps
				);
			};

			const areEqual = compare(['recordStart']);
			return areEqual;
		},
		canResetRecordStart() {
			const areFiltersEqual = this.compareFilters(this.defaultFilters);
			const areFiltersWithPaginationEqual = this.compareFilters(
				this.newFiltersWithPagination
			);

			if (!areFiltersWithPaginationEqual) {
				this.newFiltersWithPagination = { ...this.filters };
			}

			if (
				!areFiltersWithPaginationEqual &&
				!areFiltersEqual &&
				this.filters.recordStart !== this.defaultFilters.recordStart
			) {
				return true;
			}

			return false;
		},
		updateSelectedIdsMapped() {
			const existingMapped = this.selectedIdsMapped.reduce(
				(acc, item) => {
					acc[item.id] = item;
					return acc;
				},
				{}
			);

			const updatedMapped = this.selectedIds
				.map(id => {
					const log = this.errorLogsClone.items.find(
						item => item.logId === id
					);
					return log
						? {
								id: log.logId,
								resolved: log.resolved,
								ticketNumber: log.resolvedAzureDevOpsUrl
						  }
						: existingMapped[id];
				})
				.filter(item => item !== null);

			this.selectedIdsMapped = updatedMapped;
		},
		refreshErrorLog() {
			const recordStart = 1;
			this.selectedIds = [];

			if (this.filters.recordStart !== recordStart) {
				this.filters.recordStart = recordStart;
			} else {
				this.loadErrorLogs(recordStart);
			}
		},
		clickAzureTicketHandler(azureTicketNumber) {
			const path = `${devOpsDefaultUrl}${azureTicketNumber}/`;
			window.open(path, '_blank');
		},
		showReadMoreDetailsModal({ errorCode, correlationId, errorDetail }) {
			this.errorLogDetails = {
				errorCode,
				correlationId,
				errorDetail
			};

			this.showErrorLogDetailsModal = true;
		},
		getColumnRef(logId, column) {
			const refKey = `${logId}-${column}`;
			if (!this.columnRefs[refKey]) {
				this.$set(this.columnRefs, refKey, refKey);
			}
			return this.columnRefs[refKey];
		},
		isColumnTruncated(logId, column) {
			const refKey = `${logId}-${column}`;
			return this.truncatedColumns[refKey] || false;
		},
		checkAllColumnsTruncated() {
			this.$nextTick(() => {
				Object.keys(this.columnRefs).forEach(refKey => {
					const element = this.$refs[this.columnRefs[refKey]];
					if (element) {
						this.$set(
							this.truncatedColumns,
							refKey,
							element.scrollWidth > element.clientWidth
						);
					}
				});
			});
		},
		async downloadOnCSV() {
			this.downloadCSV = true;
			await this.loadErrorLogs();
			this.downloadCSV = false;
		},
		exportCSV(response) {
			const data = this.prepareErrorLogsToExport(response);
			this.convertDataToExcelFile(data);
		},
		prepareErrorLogsToExport(response) {
			const cloneErrorLogResponse = { ...response };
			let mapErrorLogs = cloneErrorLogResponse.items.map(errorLog => ({
				timestamp: this.fullDateString(errorLog.timestamp, true),
				differenceDateString: this.differenceDateString(
					errorLog.timestamp
				),
				user: errorLog.userId,
				errorLogDetail: this.generateErrorLogDetails({
					errorCode: errorLog.errorCode,
					correlationId: errorLog.correlationId,
					errorDetail: errorLog.errorDetail
				}),
				comment: errorLog.resolvedComment,
				azureTicketNumber: errorLog.resolvedAzureDevOpsUrl,
				resolved: errorLog.resolved,
				resolvedUserId: errorLog.resolvedUserId
			}));
			return mapErrorLogs;
		},
		convertDataToExcelFile(errorLogs) {
			const data = [
				[
					'Date/Time',
					'User',
					'Error Log Details',
					'Comment',
					'Azure Ticket',
					'Resolved',
					'Resolved User'
				]
			];

			const encodeCSV = value => {
				if (!value) {
					return '';
				}
				if (typeof value === 'string') {
					const escapedValue = value.replace(/"/g, '""');
					return /[",\n]/.test(value)
						? `"${escapedValue}"`
						: escapedValue;
				}
				return value.toString();
			};

			errorLogs.forEach(errorLog => {
				errorLog.errorLogDetail = errorLog.errorLogDetail.replace(
					/[\r\n]+/g,
					' '
				);
				data.push([
					encodeCSV(
						`${errorLog.timestamp}, ${errorLog.differenceDateString}`
					),

					encodeCSV(errorLog.user),
					encodeCSV(errorLog.errorLogDetail),
					encodeCSV(errorLog.comment),
					encodeCSV(errorLog.azureTicketNumber),
					encodeCSV(errorLog.resolved ? 'true' : 'false'),
					encodeCSV(errorLog.resolvedUserId)
				]);
			});
			const csvContent = data.map(row => row.join(',')).join('\n');
			const csvFilename = 'error-logs.csv';
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const downloadLink = document.createElement('a');
			downloadLink.href = URL.createObjectURL(blob);
			downloadLink.download = csvFilename;
			downloadLink.click();
			URL.revokeObjectURL(downloadLink.href);
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.updateRoute();
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}
				if (typeof newQuery.isResolved === 'string') {
					newQuery.isResolved =
						newQuery.isResolved === 'all'
							? 'all'
							: newQuery.isResolved === 'true';
				}

				const resetRecordStart = this.canResetRecordStart();

				this.filters = {
					...this.defaultFilters,
					...newQuery,
					...(resetRecordStart && {
						recordStart: this.defaultFilters.recordStart,
						maxCount: this.defaultFilters.maxCount
					})
				};
				this.loadErrorLogs(this.filters.recordStart);
			}
		},
		tableSource: {
			deep: true,
			handler() {
				this.checkAllColumnsTruncated();
			}
		}
	}
};
</script>

<style lang="scss">
/*------------------------------------*\
	Error Log Table
	\*------------------------------------*/

.navigation-bar {
	&__actions {
		cursor: pointer;
	}
}

.table-container--error-logs {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--select {
				width: 40px;
			}
			&--date-time {
				width: 120px;
			}
			&--user-id {
				width: 196px;
			}
			&--error-details,
			&--resolved-comment {
				width: 265px;
			}
			&--resolved-azure-ticket {
				width: 113px;
			}
			&--resolved {
				width: 100px;
			}
		}
	}
}

.readonly-field__label {
	display: flex;
	align-items: center;
	gap: $gutter-md;
	&--tooltip {
		text-transform: none;
		z-index: 998;
	}
}

/*------------------------------------*\
	Error Log Table - Data
\*------------------------------------*/

.error-logs-data {
	display: flex;
	flex-direction: column;

	&__select,
	&__azureDevOpsUrl,
	&__comment {
		display: flex;
		align-items: center;
		overflow-wrap: break-word;
		word-break: break-word;
		min-width: 0;
		white-space: normal;
		flex-wrap: nowrap;
		gap: $gutter-lg;
		text-transform: none;
	}
	&__user {
		text-transform: lowercase;
		word-break: break-word;
	}

	&__resolvedUser {
		text-transform: none;
	}

	&__text-emphasis {
		@include body-text('regular-emphasis');
		margin-top: $gutter-sm;
	}

	&__action {
		flex-shrink: 0;
		white-space: nowrap;
		margin-left: auto;
		.btn--icon-only {
			font-size: 22px;
		}
	}
	&__details {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;

		.error-logs-data__text_errorCode,
		.error-logs-data__text_correlationId,
		.error-logs-data__text_errorDetail {
			flex-wrap: wrap;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			max-width: 220px;
		}

		.error-logs-data__text {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.error-logs-data__action {
			flex-shrink: 0;
			white-space: nowrap;
			margin-left: auto;
		}
	}
}

.results-toolbar {
	width: 100%;
}

::v-deep .modal {
	@include mq($from: md) {
		width: 650px;
	}
}

::v-deep .modal__body {
	overflow: initial;
}

.customer-error-logs {
	&__details-modal {
		@include body-text('regular');
	}
}
</style>
